/* eslint-disable react-hooks/immutability -- Potree viewer is an imperative 3D engine object */
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { RendererContext } from '../../contexts/RendererContext';
import { isAnnotationContextGroup } from '../../contexts/AnnotationContext';
import { useAnnotationTree, useAnnotationLookup } from './useAnnotationContext';
import { useDeleteAnnotation } from './useDeleteAnnotation';
import { useCustomSelector } from '../../redux/store';
import { toggleSelectedAnnotation } from '../../redux/rendererReducer';
import { GroupType } from '../../types/graphqlTypes';
import { useDeleteConfirmation, type DeletableType } from '../../contexts/DeleteConfirmationContext';
import { useT } from '../../translation/src';

export const useRendererShortcuts = () => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const { deleteSelected, deleteWallSketchWithCascade } = useDeleteAnnotation();
  const wallSketchDeleteWarning = useT('Deleting the wall sketch also deletes all room folders and sketched objects on this floor. Room as-builts are kept. Are you sure?');
  const dispatch = useDispatch();
  const annotationsFromContext = useAnnotationTree();
  const annotationsByIdentifier = useAnnotationLookup();
  const { requiresConfirmation, requestDeleteConfirmation } = useDeleteConfirmation();
  const { selectedAnnotations } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations', 'selectedCadItems']);

  // Build a map of group identifiers to their types
  const groupTypesByIdentifier = useMemo(() => {
    const map: Record<string, GroupType | undefined> = {};
    annotationsFromContext.forEach((item) => {
      if (isAnnotationContextGroup(item)) {
        map[item.identifier] = item.type;
      }
    });
    return map;
  }, [annotationsFromContext]);
  const groupTypesByIdentifierRef = useRef(groupTypesByIdentifier);
  useEffect(() => { groupTypesByIdentifierRef.current = groupTypesByIdentifier; }, [groupTypesByIdentifier]);
  const annotationsByIdentifierRef = useRef(annotationsByIdentifier);
  useEffect(() => { annotationsByIdentifierRef.current = annotationsByIdentifier; }, [annotationsByIdentifier]);
  const selectedAnnotationsRef = useRef(selectedAnnotations);
  useEffect(() => { selectedAnnotationsRef.current = selectedAnnotations; }, [selectedAnnotations]);

  /** Resolve the DeletableType for a selected item (group type or annotation type). */
  const getDeletableType = useCallback(
    (selected: { identifier: string; type: 'group' | 'annotation' }): DeletableType | undefined => {
      if (selected.type === 'group') {
        // Room folders may lack a populated `type` — their id scheme is
        // authoritative (`roomgroup_*`, see utils/roomFolders).
        return groupTypesByIdentifierRef.current[selected.identifier] ?? (selected.identifier.startsWith('roomgroup_') ? GroupType.Room : undefined);
      }
      const annotation = annotationsByIdentifierRef.current[selected.identifier];
      return annotation?.type as DeletableType | undefined;
    },
    [],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // NOTE: Escape key handling is now managed by the persona/tool system
      // The old behavior of switching to SELECT tool on Escape is disabled
      // because we now use a different tool system with per-tool Escape handling
      if (!viewer) return;
      if (event.key === 'v') {
        viewer.orbitControls.forcedMode = 'DRAG';
      }
      if (event.key === 'b') {
        viewer.orbitControls.forcedMode = 'PAN';
      }
      if (event.key === 'Enter') {
        // intentionally empty: Enter key handling reserved for future use
      }
    },
    [viewer],
  );

  // Function to perform the actual deletion
  const performDelete = useCallback(() => {
    const currentSelected = selectedAnnotationsRef.current;
    if (currentSelected.length === 0) return;

    // Find a parent group to select after deletion (for rooms in floor groups)
    const parentGroupIdentifier = currentSelected.find((annotation) => annotation.type === 'annotation' && annotation.groupIdentifier)?.groupIdentifier;

    // Wall sketches cascade (rooms + room folders + sketch objects on their
    // floor) — route them through the cascading delete; the rest goes
    // through the plain multi-delete.
    const wallSketchIds = currentSelected.filter((selected) => getDeletableType(selected) === 'WALL_SKETCH').map((selected) => selected.identifier);
    const rest = currentSelected.filter((selected) => !wallSketchIds.includes(selected.identifier));
    for (const sketchId of wallSketchIds) {
      deleteWallSketchWithCascade(sketchId);
    }
    if (rest.length > 0) deleteSelected(rest);

    // Select the parent group if there was one
    if (parentGroupIdentifier) {
      dispatch(
        toggleSelectedAnnotation({
          identifier: parentGroupIdentifier,
          type: 'group',
          clearOtherSelections: true,
        }),
      );
    }
  }, [deleteSelected, deleteWallSketchWithCascade, getDeletableType, dispatch]);

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (!viewer) return;
      viewer.orbitControls.mode = null;
      viewer.orbitControls.forcedMode = null;
      if (!viewer.readOnly && ['Delete', 'Backspace'].includes(event.key) && event.repeat === false && !['INPUT', 'TEXTAREA'].includes((event.target as HTMLElement).nodeName)) {
        const currentSelected = selectedAnnotationsRef.current;
        if (currentSelected.length > 0) {
          const needsConfirmation = currentSelected.some((selected) => {
            return requiresConfirmation(getDeletableType(selected));
          });

          if (needsConfirmation) {
            const includesWallSketch = currentSelected.some((selected) => selected.type === 'annotation' && getDeletableType(selected) === 'WALL_SKETCH');
            requestDeleteConfirmation(performDelete, includesWallSketch ? wallSketchDeleteWarning : undefined);
          } else {
            performDelete();
          }
        }
      }
    },
    [viewer, getDeletableType, requiresConfirmation, requestDeleteConfirmation, performDelete, wallSketchDeleteWarning],
  );

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [onKeyDown, onKeyUp]);
};
